/**
 * Agent status time widget — pie or stacked bar; optional pause-reason detail for a single agent.
 */
import { useState, useEffect, useMemo, useCallback, useRef, type CSSProperties } from 'react';
import { StatusTimePortalTooltip } from './StatusTimePortalTooltip';
import { useAuth } from '@clerk/clerk-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { buildStatusTimeParams, type WidgetFilterConfig } from '../../lib/widgetScope';
import {
  STATUS_TIME_CATEGORY_ORDER,
  STATUS_TIME_CATEGORY_META,
  formatStatusTimeSeconds,
  categoryLabel,
  sortStatusDetails,
  detailChartLabel,
  detailColor,
  detailDataKey,
  type StatusDetailEntry,
} from '../../lib/statusTimeDisplay';

type AgentRow = {
  ipscape_agent_id: number;
  name: string;
  seconds_by_category: Record<string, number>;
  seconds_by_detail?: StatusDetailEntry[];
};

type StatusTimeResponse = {
  date_from: string;
  date_to: string;
  categories: string[];
  agents: AgentRow[];
  totals: Record<string, number>;
  ipscape_polling_enabled?: boolean;
};

interface WidgetAgentStatusTimeProps {
  config?: WidgetFilterConfig;
}

const REFRESH_MS = 30_000;

function needsWidgetConfig(config?: WidgetFilterConfig): boolean {
  const ft =
    config?.filter_type ??
    ((config?.ipscape_agent_id ?? '').trim()
      ? 'agent'
      : (config?.campaign_id ?? '').trim()
        ? 'campaign'
        : null);
  if (ft === 'agent') return !(config?.ipscape_agent_id ?? '').trim();
  if (ft === 'campaign') return !(config?.campaign_id ?? '').trim() && !(config?.filter_value ?? '').trim();
  if (ft === 'team') return !(config?.filter_value ?? '').trim();
  return true;
}

function agentTotalSeconds(row: AgentRow): number {
  return Object.values(row.seconds_by_category).reduce((a, b) => a + (b ?? 0), 0);
}

type LegendItem = { label: string; color: string; seconds: number };

const TOOLTIP_BOX_STYLE: CSSProperties = {
  background: '#0f172a',
  border: '1px solid #64748b',
  borderRadius: 8,
  padding: '0.5rem 0.65rem',
  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.65)',
  fontSize: '0.75rem',
  lineHeight: 1.45,
  color: '#f1f5f9',
  zIndex: 50,
  pointerEvents: 'none',
  minWidth: 168,
};

function StatusTimeTooltipBody({
  title,
  items,
  totalSec,
}: {
  title?: string;
  items: { label: string; color: string; seconds: number }[];
  totalSec: number;
}) {
  const visible = items.filter((i) => i.seconds > 0).sort((a, b) => b.seconds - a.seconds);
  if (visible.length === 0) return null;
  return (
    <div style={TOOLTIP_BOX_STYLE}>
      {title ? (
        <div style={{ fontWeight: 600, marginBottom: '0.35rem', color: '#f8fafc' }}>{title}</div>
      ) : null}
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {visible.map((item) => {
          const pct = totalSec > 0 ? Math.round((item.seconds / totalSec) * 100) : 0;
          return (
            <li
              key={item.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                marginBottom: '0.2rem',
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: item.color,
                  border: '1px solid rgba(255,255,255,0.25)',
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1, color: '#e2e8f0' }}>{item.label}</span>
              <span style={{ color: '#f8fafc', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {formatStatusTimeSeconds(item.seconds)}
                {pct > 0 ? ` (${pct}%)` : ''}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type TooltipPayloadItem = {
  value?: number | string;
  name?: string;
  dataKey?: string | number;
  color?: string;
};

function buildTooltipItems(
  payload: TooltipPayloadItem[] | undefined,
  grandTotal: number,
  useDetailCharts: boolean,
  detailSegments: StatusDetailEntry[],
  title?: string
) {
  if (!payload?.length) return null;
  const items = payload
    .map((p) => {
      const sec = Number(p.value) || 0;
      if (sec <= 0) return null;
      let itemLabel = String(p.name ?? p.dataKey ?? '');
      let color = typeof p.color === 'string' ? p.color : '#94a3b8';
      if (useDetailCharts) {
        const key = String(p.dataKey ?? '');
        const d = detailSegments.find((x) => detailDataKey(x) === key);
        if (d) {
          itemLabel = detailChartLabel(d);
          color = detailColor(d.parent_category, d.detail_key, d.detail_label);
        }
      } else {
        const cat = String(p.dataKey ?? '');
        itemLabel = categoryLabel(cat);
        color =
          STATUS_TIME_CATEGORY_META[cat as keyof typeof STATUS_TIME_CATEGORY_META]?.color ?? color;
      }
      return { label: itemLabel, color, seconds: sec };
    })
    .filter((x): x is { label: string; color: string; seconds: number } => x != null);

  return (
    <StatusTimeTooltipBody
      title={typeof title === 'string' && title.trim() ? title : undefined}
      items={items}
      totalSec={grandTotal}
    />
  );
}

function StatusTimeLegend({ items }: { items: LegendItem[] }) {
  const visible = items.filter((i) => i.seconds > 0);
  if (visible.length === 0) return null;
  return (
    <ul
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.35rem 0.75rem',
        margin: 0,
        padding: '0.15rem 0 0',
        listStyle: 'none',
        fontSize: 'clamp(0.58rem, 2.2cqi, 0.68rem)',
        flexShrink: 0,
      }}
    >
      {visible.map((item) => (
        <li
          key={item.label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            color: 'var(--text-muted)',
            maxWidth: '100%',
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: 2,
              background: item.color,
              border: '1px solid rgba(255,255,255,0.2)',
              flexShrink: 0,
            }}
          />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function WidgetAgentStatusTime({ config }: WidgetAgentStatusTimeProps) {
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const { getToken } = useAuth();
  const [data, setData] = useState<StatusTimeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const view = config?.time_view === 'pie' ? 'pie' : 'bar';
  const misconfigured = needsWidgetConfig(config);

  const fetchData = useCallback(async () => {
    if (misconfigured) {
      setLoading(false);
      setData(null);
      setError(null);
      return;
    }
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const params = buildStatusTimeParams(config);
    try {
      const res = await fetch(`${base}/api/agents/status-time?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as { error?: string }).error || `HTTP ${res.status}`);
        setData(null);
        return;
      }
      const json = (await res.json()) as StatusTimeResponse;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getToken, config, misconfigured]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      await fetchData();
      if (cancelled) return;
    })();
    const id = window.setInterval(() => {
      void fetchData();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchData]);

  const activeCategories = useMemo(() => {
    if (!data) return [...STATUS_TIME_CATEGORY_ORDER];
    return STATUS_TIME_CATEGORY_ORDER.filter((cat) => (data.totals[cat] ?? 0) > 0);
  }, [data]);

  const chartRows = useMemo(() => {
    if (!data?.agents.length) return [];
    return [...data.agents]
      .sort((a, b) => agentTotalSeconds(b) - agentTotalSeconds(a))
      .map((a) => {
        const row: Record<string, string | number> = { name: a.name };
        for (const cat of STATUS_TIME_CATEGORY_ORDER) {
          row[cat] = a.seconds_by_category[cat] ?? 0;
        }
        return row;
      });
  }, [data]);

  const singleAgent = (data?.agents.length ?? 0) === 1;
  const primaryAgent = singleAgent ? data?.agents[0] : undefined;
  const showDetail = config?.time_detail !== false && singleAgent;
  const detailSegments = useMemo(() => {
    if (!showDetail || !primaryAgent?.seconds_by_detail?.length) return [];
    return sortStatusDetails(primaryAgent.seconds_by_detail);
  }, [showDetail, primaryAgent?.seconds_by_detail]);
  const useDetailCharts = showDetail && detailSegments.length > 0;

  const pieData = useMemo(() => {
    if (!data) return [];
    if (useDetailCharts && primaryAgent) {
      return detailSegments.map((d) => ({
        name: detailChartLabel(d),
        value: d.seconds,
        fill: detailColor(d.parent_category, d.detail_key, d.detail_label),
        detail: d,
      }));
    }
    return activeCategories.map((cat) => ({
      name: STATUS_TIME_CATEGORY_META[cat].label,
      value: data.totals[cat] ?? 0,
      fill: STATUS_TIME_CATEGORY_META[cat].color,
    }));
  }, [data, activeCategories, useDetailCharts, detailSegments, primaryAgent]);

  const grandTotal = useMemo(() => {
    if (!data) return 0;
    if (useDetailCharts && primaryAgent) {
      return detailSegments.reduce((a, d) => a + d.seconds, 0);
    }
    if (singleAgent && primaryAgent) return agentTotalSeconds(primaryAgent);
    return Object.values(data.totals).reduce((a, b) => a + (b ?? 0), 0);
  }, [data, useDetailCharts, detailSegments, primaryAgent, singleAgent]);

  const detailBarRow = useMemo(() => {
    if (!useDetailCharts || !primaryAgent) return null;
    const row: Record<string, string | number> = { name: primaryAgent.name };
    for (const d of detailSegments) {
      row[detailDataKey(d)] = d.seconds;
    }
    return row;
  }, [useDetailCharts, primaryAgent, detailSegments]);

  const legendItems = useMemo((): LegendItem[] => {
    if (useDetailCharts) {
      return detailSegments.map((d) => ({
        label: detailChartLabel(d),
        color: detailColor(d.parent_category, d.detail_key, d.detail_label),
        seconds: d.seconds,
      }));
    }
    if (!data) return [];
    return activeCategories.map((cat) => ({
      label: STATUS_TIME_CATEGORY_META[cat].label,
      color: STATUS_TIME_CATEGORY_META[cat].color,
      seconds: data.totals[cat] ?? 0,
    }));
  }, [useDetailCharts, detailSegments, data, activeCategories]);

  const barSegmentStroke = 'var(--bg, #0f172a)';

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.35rem 0.5rem',
        containerType: 'size',
      }}
    >
      {misconfigured && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          Configure widget: choose a team, campaign, or agent.
        </p>
      )}
      {!misconfigured && loading && !data && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          Loading…
        </p>
      )}
      {!misconfigured && error && (
        <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          {error}
        </p>
      )}
      {!misconfigured && !loading && !error && data?.ipscape_polling_enabled === false && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center', lineHeight: 1.45 }}>
          ipSCAPE polling is turned off. Enable it under Settings → Operating hours to record status time.
        </p>
      )}
      {!misconfigured && !loading && !error && data?.ipscape_polling_enabled !== false && grandTotal === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center', lineHeight: 1.45 }}>
          Collecting status time… usually appears within 1–2 minutes while agents are active on UC. Refreshes every 30s.
        </p>
      )}
      {!misconfigured && !error && grandTotal > 0 && view === 'pie' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div ref={chartAreaRef} style={{ flex: '1 1 58%', height: '100%', minWidth: 0, minHeight: 100 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="88%"
                  stroke="var(--widget-bg)"
                  strokeWidth={1}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  cursor={false}
                  wrapperStyle={{ visibility: 'hidden' }}
                  content={({ active, payload, coordinate }) => {
                    const pieItems =
                      payload?.map((p) => {
                        const sec = Number(p.value) || 0;
                        const pl = p.payload as { name?: string; fill?: string; detail?: StatusDetailEntry };
                        const label = pl?.detail
                          ? detailChartLabel(pl.detail)
                          : (pl?.name ?? String(p.name ?? ''));
                        const color =
                          pl?.fill ?? (typeof p.color === 'string' ? p.color : '#94a3b8');
                        return { label, color, seconds: sec };
                      }) ?? [];
                    return (
                      <StatusTimePortalTooltip
                        active={active}
                        coordinate={coordinate}
                        chartRef={chartAreaRef}
                      >
                        <StatusTimeTooltipBody items={pieItems} totalSec={grandTotal} />
                      </StatusTimePortalTooltip>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul
            style={{
              flex: '0 1 42%',
              margin: 0,
              padding: 0,
              listStyle: 'none',
              fontSize: 'clamp(0.6rem, 2.5cqi, 0.75rem)',
              overflow: 'auto',
              maxHeight: '100%',
            }}
          >
            {pieData.map((d) => (
              <li
                key={d.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  marginBottom: '0.25rem',
                  color: 'var(--text-muted)',
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: d.fill, flexShrink: 0 }} />
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.name}
                </span>
                <span style={{ marginLeft: 'auto', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                  {formatStatusTimeSeconds(d.value)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!misconfigured && !error && grandTotal > 0 && view === 'bar' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <div ref={chartAreaRef} style={{ flex: 1, minHeight: 72, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={useDetailCharts && detailBarRow ? [detailBarRow] : chartRows}
                layout="vertical"
                margin={{
                  top: 4,
                  right: 8,
                  left: singleAgent ? 8 : 92,
                  bottom: 8,
                }}
                barCategoryGap={singleAgent ? '35%' : '16%'}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--header-border)" horizontal={!singleAgent} vertical />
                <XAxis
                  type="number"
                  tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                  tickFormatter={(v) => formatStatusTimeSeconds(Number(v))}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={singleAgent ? 0 : 88}
                  hide={singleAgent}
                  tick={{ fill: 'var(--text)', fontSize: 10 }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(148, 163, 184, 0.15)' }}
                  wrapperStyle={{ visibility: 'hidden' }}
                  content={({ active, payload, label, coordinate }) => (
                    <StatusTimePortalTooltip
                      active={active}
                      coordinate={coordinate}
                      chartRef={chartAreaRef}
                    >
                      {buildTooltipItems(
                        payload as unknown as TooltipPayloadItem[] | undefined,
                        grandTotal,
                        useDetailCharts,
                        detailSegments,
                        typeof label === 'string' ? label : undefined
                      )}
                    </StatusTimePortalTooltip>
                  )}
                />
                {useDetailCharts
                  ? detailSegments.map((d) => (
                      <Bar
                        key={detailDataKey(d)}
                        dataKey={detailDataKey(d)}
                        name={detailChartLabel(d)}
                        stackId="status"
                        fill={detailColor(d.parent_category, d.detail_key, d.detail_label)}
                        stroke={barSegmentStroke}
                        strokeWidth={1}
                      />
                    ))
                  : activeCategories.map((cat) => (
                      <Bar
                        key={cat}
                        dataKey={cat}
                        name={STATUS_TIME_CATEGORY_META[cat].label}
                        stackId="status"
                        fill={STATUS_TIME_CATEGORY_META[cat].color}
                        stroke={barSegmentStroke}
                        strokeWidth={1}
                      />
                    ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <StatusTimeLegend items={legendItems} />
          <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>
            {formatStatusTimeSeconds(grandTotal)} total
            {singleAgent && data?.agents[0] ? ` · ${data.agents[0].name}` : ''}
            {useDetailCharts ? ' · pause reasons & sub-states' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
